/**
 * <Sheet> — Slide drawer from any edge of the viewport.
 *
 * Built on @radix-ui/react-dialog with custom slide animations per side.
 * Use for:
 *   - Mobile bottom sheets (Forms, Action sheets, Detail panels)
 *   - Desktop side panels (Filters, Settings, Inspectors)
 *   - Top sheets (rare; notifications expanded)
 *
 * Composition-based API:
 *
 *   <Sheet.Root open={...} onOpenChange={...}>
 *     <Sheet.Trigger asChild>
 *       <Button>Open</Button>
 *     </Sheet.Trigger>
 *     <Sheet.Content side="bottom" size="md" title="Edit Field">
 *       <Sheet.Body>
 *         {form fields}
 *       </Sheet.Body>
 *       <Sheet.Footer>
 *         <Sheet.Close asChild>
 *           <Button variant="secondary">Cancel</Button>
 *         </Sheet.Close>
 *         <Button onClick={handleSave}>Save</Button>
 *       </Sheet.Footer>
 *     </Sheet.Content>
 *   </Sheet.Root>
 *
 * Sizes by side:
 * - side="bottom" + size="sm": 40vh
 * - side="bottom" + size="md": 60vh (default)
 * - side="bottom" + size="lg": 80vh
 * - side="bottom" + size="full": 95vh
 * - side="right" / "left" + size="md": 500px (default)
 * - side="right" / "left" + size="full": 90vw
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.8
 */

import React, { useId } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useOverlayPortal } from './useOverlayPortal';

// Side + size -> Tailwind classes mapping
function getContentClasses(side, size) {
  const baseClasses = 'fixed bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] shadow-[var(--aif-shadow-elevated)] border-[var(--aif-border-default)] focus:outline-none flex flex-col';

  const sideMap = {
    bottom: 'bottom-0 left-0 right-0 border-t rounded-t-[var(--aif-radius-lg)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
    top: 'top-0 left-0 right-0 border-b rounded-b-[var(--aif-radius-lg)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
    right: 'right-0 top-0 bottom-0 border-l data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
    left: 'left-0 top-0 bottom-0 border-r data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  };

  const sizeMap = {
    bottom: { sm: 'h-[40vh]', md: 'h-[60vh]', lg: 'h-[80vh]', full: 'h-[95vh]' },
    top: { sm: 'h-[40vh]', md: 'h-[60vh]', lg: 'h-[80vh]', full: 'h-[95vh]' },
    right: { sm: 'w-[320px]', md: 'w-[500px]', lg: 'w-[700px]', full: 'w-[90vw]' },
    left: { sm: 'w-[320px]', md: 'w-[500px]', lg: 'w-[700px]', full: 'w-[90vw]' },
  };

  const sideClass = sideMap[side] || sideMap.bottom;
  const sizeClass = (sizeMap[side] || sizeMap.bottom)[size] || (sizeMap[side] || sizeMap.bottom).md;

  return `${baseClasses} ${sideClass} ${sizeClass}`;
}

/**
 * Sheet.Root — state context.
 */
function SheetRoot({ open, defaultOpen, onOpenChange, children }) {
  return (
    <Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {children}
    </Dialog.Root>
  );
}
SheetRoot.displayName = 'Sheet.Root';

const SheetTrigger = Dialog.Trigger;
const SheetClose = Dialog.Close;

/**
 * Sheet.Content — sliding panel.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'bottom'|'top'|'left'|'right'} [props.side='bottom']
 * @param {'sm'|'md'|'lg'|'full'} [props.size='md']
 * @param {string} [props.title] - Renders as Sheet.Title automatically
 * @param {boolean} [props.dismissible=true]
 * @param {boolean} [props.showCloseButton=true]
 * @param {string} [props.className]
 */
function SheetContent({
  children,
  side = 'bottom',
  size = 'md',
  title,
  dismissible = true,
  showCloseButton = true,
  className = '',
  ariaLabelledBy,
  ariaDescribedBy,
  onInteractOutside,
  onEscapeKeyDown,
  ...rest
}) {
  const portalContainer = useOverlayPortal();
  const autoTitleId = useId();
  const effectiveLabelledBy = ariaLabelledBy || (title ? autoTitleId : undefined);

  if (process.env.NODE_ENV !== 'production') {
    if (!title && !ariaLabelledBy) {
      console.warn(
        '[Sheet.Content] No title prop or ariaLabelledBy. ' +
        'Screen readers require an accessible name.'
      );
    }
  }

  const handleInteractOutside = (e) => {
    if (!dismissible) e.preventDefault();
    if (onInteractOutside) onInteractOutside(e);
  };

  const handleEscapeKeyDown = (e) => {
    if (!dismissible) e.preventDefault();
    if (onEscapeKeyDown) onEscapeKeyDown(e);
  };

  // z-index: full-page sheets get page-fullscreen tier, others get bottom-sheet tier
  const zIndex = size === 'full' ? 'var(--aif-z-page-fullscreen)' : 'var(--aif-z-bottom-sheet)';

  const contentClasses = getContentClasses(side, size);

  return (
    <Dialog.Portal container={portalContainer}>
      <Dialog.Overlay
        className="fixed inset-0 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        style={{ zIndex }}
      />
      <Dialog.Content
        className={`${contentClasses} ${className}`}
        style={{ zIndex }}
        aria-labelledby={effectiveLabelledBy}
        aria-describedby={ariaDescribedBy}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        {...rest}
      >
        {/* Drag handle for bottom sheets */}
        {side === 'bottom' && (
          <div className="flex-shrink-0 flex justify-center py-2" aria-hidden="true">
            <div className="w-10 h-1 rounded-full bg-[var(--aif-border-default)]" />
          </div>
        )}

        {(title || showCloseButton) && (
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--aif-border-default)]">
            {title ? (
              <Dialog.Title id={autoTitleId} className="text-lg font-semibold text-[var(--aif-text-primary)] flex-1 min-w-0 truncate">
                {title}
              </Dialog.Title>
            ) : (
              <span className="flex-1" />
            )}
            {showCloseButton && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="flex-shrink-0 p-1 -m-1 rounded-md text-[var(--aif-text-muted)] hover:text-[var(--aif-text-primary)] hover:bg-[var(--aif-bg-tertiary)] transition-colors focus:outline-none focus-visible:ring-2 focus:ring-[var(--aif-color-primary)]"
                  aria-label="Schließen"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 5L15 15M15 5L5 15" />
                  </svg>
                </button>
              </Dialog.Close>
            )}
          </div>
        )}

        {children}
      </Dialog.Content>
    </Dialog.Portal>
  );
}
SheetContent.displayName = 'Sheet.Content';

/**
 * Sheet.Title — explicit title element.
 */
function SheetTitle({ children, className = '', ...rest }) {
  return (
    <Dialog.Title
      className={`text-lg font-semibold text-[var(--aif-text-primary)] ${className}`}
      {...rest}
    >
      {children}
    </Dialog.Title>
  );
}
SheetTitle.displayName = 'Sheet.Title';

/**
 * Sheet.Description — accessible description.
 */
function SheetDescription({ children, className = '', ...rest }) {
  return (
    <Dialog.Description
      className={`text-sm text-[var(--aif-text-secondary)] ${className}`}
      {...rest}
    >
      {children}
    </Dialog.Description>
  );
}
SheetDescription.displayName = 'Sheet.Description';

/**
 * Sheet.Body — scrollable content area.
 */
function SheetBody({ children, className = '' }) {
  return (
    <div className={`flex-1 overflow-y-auto px-6 py-4 ${className}`}>
      {children}
    </div>
  );
}
SheetBody.displayName = 'Sheet.Body';

/**
 * Sheet.Footer — action row at the bottom (or top for top sheets).
 */
function SheetFooter({ children, className = '' }) {
  return (
    <div className={`flex-shrink-0 px-6 py-4 border-t border-[var(--aif-border-default)] flex justify-end gap-3 ${className}`}>
      {children}
    </div>
  );
}
SheetFooter.displayName = 'Sheet.Footer';

// Public API
export const Sheet = {
  Root: SheetRoot,
  Trigger: SheetTrigger,
  Close: SheetClose,
  Content: SheetContent,
  Title: SheetTitle,
  Description: SheetDescription,
  Body: SheetBody,
  Footer: SheetFooter,
};
